import type { Allergen, Diet, FoodFlag, FoodGroup, FoodItem } from "./types";

/**
 * The words a food row is tagged with, what each means, and the questions the planner and the
 * questionnaire ask of them. (ADR-038)
 *
 * The tags are derived at build time by `scripts/build-foods.js` — for INDB's dishes from the
 * recipe sheet's ingredient codes, for the staples and FNDDS's Indian dishes from a list reviewed by
 * hand, for the rest of USDA from its own food category — and corrected per row in
 * `domain/foodMeta.ts::TAG_OVERRIDES`. The vocabulary here and the script's must agree; a test
 * reads the generated file and fails on any word this module does not know.
 *
 * **Absent is "not known", never "no".** A row without `al` has no allergen list, which is not
 * the same as an empty one; a row without `vegan` in `fl` is not known to be vegan, which is not
 * the same as known not to be. Every question below therefore answers `true`, `false` or `null`,
 * and anything that keeps food *out* — an allergy, a diet, a fast — treats `null` as out. That is
 * invariant 18's rule for values, applied to tags.
 */

export const FOOD_GROUPS: readonly FoodGroup[] = [
  "grain", "legume", "veg", "leafy", "fruit", "dairy", "egg", "meat", "fish", "nut", "fat",
  "sweet", "fried", "drink", "other",
];

/** How a group reads on screen — the DGI 2024 groups in the words a kitchen uses. */
export const GROUP_LABEL: Readonly<Record<FoodGroup, string>> = {
  grain: "Grains",
  legume: "Dal and legumes",
  veg: "Vegetables",
  leafy: "Leafy greens",
  fruit: "Fruit",
  dairy: "Milk and curd",
  egg: "Egg",
  meat: "Meat",
  fish: "Fish",
  nut: "Nuts and seeds",
  fat: "Oil and fat",
  sweet: "Sweets",
  fried: "Fried snacks",
  drink: "Drinks",
  other: "Other",
};

export const DIETS: readonly Diet[] = ["veg", "egg", "nonveg"];

export const DIET_LABEL: Readonly<Record<Diet, string>> = {
  veg: "Vegetarian",
  egg: "Has egg",
  nonveg: "Non-veg",
};

/** The eight the "never eat" question offers, in its order (the EatingAvoid board). */
export const ALLERGENS: readonly Allergen[] = [
  "peanut", "treenut", "milk", "wheat", "egg", "soy", "sesame", "fish",
];

export const ALLERGEN_LABEL: Readonly<Record<Allergen, string>> = {
  peanut: "Peanut",
  treenut: "Tree nuts",
  milk: "Milk",
  wheat: "Wheat / gluten",
  egg: "Egg",
  soy: "Soy",
  sesame: "Sesame",
  fish: "Fish / shellfish",
};

export const FOOD_FLAGS: readonly FoodFlag[] = ["vegan", "jain", "vrat", "soft", "tiffin", "onepot"];

/** What each flag claims, when it is present. */
export const FLAG_MEANING: Readonly<Record<FoodFlag, string>> = {
  vegan: "nothing from an animal — no milk, ghee, butter, honey or egg",
  jain: "vegetarian with nothing grown underground, no onion or garlic, no mushroom, no honey",
  vrat: "fast-day food: no grain but the fast-day flours, no pulses, onion or garlic",
  soft: "easy to chew — dal, khichdi, curd, idli, porridge",
  tiffin: "keeps and travels in a lunch box",
  onepot: "a meal in one pot — khichdi, pulao, upma",
};

export function isFoodGroup(s: unknown): s is FoodGroup {
  return typeof s === "string" && (FOOD_GROUPS as readonly string[]).includes(s);
}
export function isDiet(s: unknown): s is Diet {
  return typeof s === "string" && (DIETS as readonly string[]).includes(s);
}
export function isAllergen(s: unknown): s is Allergen {
  return typeof s === "string" && (ALLERGENS as readonly string[]).includes(s);
}
export function isFoodFlag(s: unknown): s is FoodFlag {
  return typeof s === "string" && (FOOD_FLAGS as readonly string[]).includes(s);
}

/* ---------- the questions ---------- */

/** The eating patterns a person can have. `egg` is vegetarian plus eggs, the usual Indian sense. */
export type EatingPattern = "veg" | "egg" | "nonveg" | "vegan" | "jain";

/**
 * Whether a food fits an eating pattern: true, false, or null when the row cannot say.
 *
 * Non-veg fits everything, so it answers true even for an untagged row — there is nothing it
 * could rule out.
 */
export function fitsPattern(f: Pick<FoodItem, "dt" | "fl" | "al">, p: EatingPattern): boolean | null {
  if (p === "nonveg") return true;
  if (p === "egg") return f.dt === undefined ? null : f.dt !== "nonveg";
  if (p === "veg") return f.dt === undefined ? null : f.dt === "veg";
  if (f.dt === "egg" || f.dt === "nonveg") return false;
  if (p === "vegan") {
    if (f.fl?.includes("vegan")) return true;
    if (f.al?.includes("milk") || f.al?.includes("egg")) return false;
    return null;
  }
  return f.fl?.includes("jain") ? true : null;
}

/** Whether a food contains an allergen: true, false, or null when it has no allergen list. */
export function contains(f: Pick<FoodItem, "al">, a: Allergen): boolean | null {
  if (!Array.isArray(f.al)) return null;
  return f.al.includes(a);
}

/** True when the row carries the flag; false only means "not known to be". */
export function hasFlag(f: Pick<FoodItem, "fl">, flag: FoodFlag): boolean {
  return !!f.fl?.includes(flag);
}

/**
 * The hard filter: true only when the food is *known* to fit the pattern and *known* to be free
 * of every allergen to avoid. A row that cannot say is out — a planner that offers peanut chikki
 * to someone with a peanut allergy because the row was untagged has failed at the one thing it
 * must not get wrong.
 */
export function knownSafe(
  f: Pick<FoodItem, "dt" | "fl" | "al">,
  opts: { pattern?: EatingPattern; avoid?: readonly Allergen[] }
): boolean {
  if (opts.pattern && fitsPattern(f, opts.pattern) !== true) return false;
  for (const a of opts.avoid ?? []) if (contains(f, a) !== false) return false;
  return true;
}

/* ---------- dish families ----------

   A dislike can name a family rather than one row: "Bhindi — every dish" keeps out the boiled
   okra, the bharwa bhindi and the bhindi sabzi, and whatever bhindi dish a rebuilt table adds
   next year. A family is matched on the name — the table's rows and the user's own foods alike —
   so it survives a rebuild where a list of ids would not. */

export interface FoodFamily {
  key: string;
  label: string;
  /** whole words (a trailing "s" allowed) that put a name in the family */
  words: readonly string[];
  /** names that carry a word but are not the family: "lemon curd" is not curd */
  not?: RegExp;
}

export const FOOD_FAMILIES: readonly FoodFamily[] = [
  { key: "karela", label: "Karela", words: ["karela", "karele", "bitter gourd", "bittergourd", "bitter melon"] },
  { key: "baingan", label: "Baingan", words: ["baingan", "brinjal", "eggplant", "aubergine", "begun"] },
  { key: "lauki", label: "Lauki", words: ["lauki", "ghiya", "ghia", "bottle gourd", "dudhi", "doodhi"] },
  { key: "tinda", label: "Tinda", words: ["tinda", "tinde", "round gourd"] },
  { key: "mushroom", label: "Mushroom", words: ["mushroom", "khumb"] },
  { key: "paneer", label: "Paneer", words: ["paneer", "paaner", "cottage cheese"] },
  { key: "rajma", label: "Rajma", words: ["rajma", "rajmah", "kidney bean"] },
  { key: "chana", label: "Chana", words: ["chana", "channa", "chane", "chole", "chhole", "chickpea", "kabuli", "garbanzo"] },
  { key: "oats", label: "Oats", words: ["oats", "oat", "oatmeal"] },
  { key: "curd", label: "Curd", words: ["curd", "dahi", "yogurt", "yoghurt"], not: /lemon curd/ },
  { key: "sprouts", label: "Sprouts", words: ["sprout", "sprouted"] },
  { key: "soya", label: "Soya chunks", words: ["soya chunk", "soy chunk", "nutrinugget", "nutri nugget"] },
  { key: "fish", label: "Fish", words: ["fish", "machli", "machhi", "pomfret", "rohu", "mackerel", "surmai"] },
  { key: "egg", label: "Egg", words: ["egg", "anda", "ande", "omelette", "omlet", "omelet"] },
  { key: "chicken", label: "Chicken", words: ["chicken", "murg", "murgh"] },
  { key: "mutton", label: "Mutton", words: ["mutton", "lamb", "gosht", "keema", "minced meat"] },
  { key: "prawn", label: "Prawn", words: ["prawn", "jhinga", "shrimp"] },
  { key: "methi", label: "Methi", words: ["methi", "fenugreek"] },
  { key: "palak", label: "Palak", words: ["palak", "spinach"] },
  { key: "raita", label: "Raita", words: ["raita"] },
  { key: "upma", label: "Upma", words: ["upma", "uppma"] },
  { key: "dalia", label: "Dalia", words: ["dalia", "daliya", "broken wheat", "cracked wheat"] },
  { key: "bhindi", label: "Bhindi", words: ["bhindi", "okra", "lady's finger", "ladies finger", "lady finger"] },
  { key: "kadhi", label: "Kadhi", words: ["kadhi", "kadi", "karhi"] },
  { key: "arbi", label: "Arbi", words: ["arbi", "colocasia", "taro"] },
  { key: "poha", label: "Poha", words: ["poha", "rice flakes", "aval"] },
  { key: "khichdi", label: "Khichdi", words: ["khichdi", "khichri", "khitchdi", "khichadi"] },
];

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Whether `name` has `word` as a whole word (a plural "s" or "es" allowed). */
export function nameHasWord(name: string, word: string): boolean {
  const w = word.trim().toLowerCase();
  if (!w) return false;
  return new RegExp(`(^|[^a-z])${escapeRe(w)}(e?s)?($|[^a-z])`).test(name.toLowerCase());
}

export function familyByKey(key: string): FoodFamily | null {
  return FOOD_FAMILIES.find((f) => f.key === key) ?? null;
}

export function inFamily(name: string, fam: FoodFamily): boolean {
  if (fam.not?.test(name.toLowerCase())) return false;
  return fam.words.some((w) => nameHasWord(name, w));
}

/** Every family a name belongs to, by key. */
export function familiesOf(name: string): string[] {
  return FOOD_FAMILIES.filter((f) => inFamily(name, f)).map((f) => f.key);
}

/** The families a typed search could mean — "bhin" is Bhindi, "okr" is Bhindi too. */
export function familiesMatching(query: string): FoodFamily[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  return FOOD_FAMILIES.filter(
    (f) => f.label.toLowerCase().startsWith(q) || f.words.some((w) => w.startsWith(q))
  );
}
